import { color } from "@repo/fika-design-system/color"
import {
  Chart,
  Colors,
  LinearScale,
  Tooltip,
  type TooltipModel,
} from "chart.js"
import { format } from "date-fns"
import { isDeepEqual } from "remeda"
import { Bar } from "solid-chartjs"
import {
  createMemo,
  createResource,
  createSignal,
  Match,
  onMount,
  Show,
  Switch,
} from "solid-js"
import { z } from "zod"
import { useAppContext } from "@/context/app"
import { Mini } from "@/designSystem/components/mini/mini"
import { Tooltip as TooltipComponent } from "@/designSystem/components/tooltip"
import {
  INTERVAL,
  type Interval,
  MEASURES,
  type Measure,
} from "@/routes/posts-router"
import { Box } from "../components/box"
import { Icon } from "../components/icon"
import {
  chartContainerRecipe,
  chartRecipe,
  containerClass,
  controlsClass,
  dateRangeClass,
  deltaRecipe,
  statNumClass,
  statRecipe,
  statsClass,
  statsHeaderClass,
  statTextClass,
} from "./stats-header.css"

const dataSchema = z.object({
  counts: z.object({
    views: z.number(),
    visitors: z.number(),
    bounces: z.number(),
  }),
  countsPrevious: z.object({
    views: z.number(),
    visitors: z.number(),
    bounces: z.number(),
  }),
  chartData: z.array(
    z.object({
      date: z.string(),
      views: z.number(),
      visitors: z.number(),
    }),
  ),
  intervalType: z.union([z.literal("HOUR"), z.literal("DAY")]),
})

type Props = {
  path?: string
  interval: Interval
  measure: Measure
  onChange?: (change: { interval?: Interval; measure?: Measure }) => void
}

export function StatsHeader(props: Props) {
  const { session } = useAppContext()

  onMount(() => {
    Chart.register(Tooltip, Colors, LinearScale)
  })

  const apiParams = createMemo(() => {
    const params: Record<string, string> = {
      interval: props.interval,
      measure: props.measure,
    }
    if (props.path) params.path = props.path

    return new URLSearchParams(params)
  })

  const [getStats] = createResource(apiParams, async params => {
    const backendUrl = import.meta.env.VITE_FIKA_BACKEND_URL

    const response = await fetch(`${backendUrl}/api/chart_stats?${params}`, {
      headers: { Authorization: `Bearer ${session.jwt}` },
    })
    const data = await response.json()
    return dataSchema.safeParse(data).data ?? null
  })

  const deltaPercentage = createMemo(() => {
    const stats = getStats()
    if (!stats) return 0

    const views = stats.counts.views
    const viewsPrevious = stats.countsPrevious.views
    if (viewsPrevious === 0 && views === 0) return 0

    return Number((((views - viewsPrevious) / viewsPrevious) * 100).toFixed(1))
  })

  const bigNumber = createMemo(() => {
    const stats = getStats()
    if (!stats) return 0

    const ms = props.measure

    return stats.counts[ms]
  })

  const period = createMemo(() => {
    switch (props.interval) {
      case "today":
        return "today"
      case "yesterday":
        return "yesterday"
      case "1d":
        return "last 24 hours"
      case "7d":
        return "last 7 days"
      case "30d":
        return "last 30 days"
      case "90d":
        return "last 90 days"
      default:
        throw new Error("Invalid interval")
    }
  })

  const chartData = createMemo(
    (prev: any | null) => {
      const stats = getStats()
      if (!stats) return prev
      const intervalType = stats.intervalType

      const labels = stats.chartData.map(row => {
        if (intervalType === "HOUR") {
          return format(row.date, "p")
        }

        return format(row.date, "PP")
      })
      const data = stats.chartData.map(row => row[props.measure] ?? 0)

      return {
        labels,
        datasets: [
          {
            label: props.measure,
            data,
            backgroundColor:
              props.measure === "views" ? color.amber400 : color.emerald400,
          },
        ],
      }
    },
    null,
    { equals: isDeepEqual },
  )

  const getDateRange = createMemo(() => {
    const stats = getStats()
    if (!stats) return null

    const firstDate = stats.chartData[0]?.date
    const lastDate = stats.chartData[stats.chartData.length - 1]?.date
    if (!firstDate || !lastDate) return null

    const intervalType = stats.intervalType
    const formatStr = intervalType === "HOUR" ? "p" : "PP"

    return {
      start: format(new Date(firstDate), formatStr),
      end: format(new Date(lastDate), formatStr),
    }
  })

  const [hoveredValue, setHoveredValue] = createSignal<number | null>(null)
  const [hoveredLabel, setHoveredLabel] = createSignal<string | null>(null)

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    color: color.sand800,
    plugins: {
      tooltip: {
        enabled: false,
        mode: "index",
        intersect: false,
        external: (context: { tooltip: TooltipModel<"bar"> }) => {
          const tooltip = context.tooltip
          if (tooltip.opacity === 0) {
            setHoveredValue(null)
            setHoveredLabel(null)
            return
          }

          const dataPoint = tooltip.dataPoints?.[0]
          if (!dataPoint?.formattedValue || !dataPoint.label) return

          const value = Number(dataPoint.formattedValue)
          const label = dataPoint.label
          setHoveredValue(value)
          setHoveredLabel(label)
        },
      },
    },
    layout: { padding: 0, autoPadding: false },
    scales: {
      x: { display: false },
      y: { display: false },
    },
  }

  const displayNumber = createMemo(() => {
    if (getStats.loading) return "?"
    return hoveredValue() ?? bigNumber()
  })

  const displayPeriod = createMemo(() => {
    return hoveredLabel() ?? period()
  })

  return (
    <div class={containerClass}>
      <div class={statsHeaderClass}>
        <Show when={props.onChange}>
          {onChange => (
            <div class={controlsClass}>
              <Mini.Menu>
                <TooltipComponent label="measure">
                  <Mini.Section>
                    <Mini.Select
                      value={props.measure}
                      onChange={measure =>
                        onChange()({ measure: measure as Measure })
                      }
                      hideValue
                      items={MEASURES.map(value => ({
                        value,
                        label: value,
                        iconName: value === "views" ? "eye" : "user",
                      }))}
                    />
                  </Mini.Section>
                </TooltipComponent>
                <TooltipComponent label="interval">
                  <Mini.Section>
                    <Mini.Select
                      items={Object.values(INTERVAL).map(value => ({
                        value,
                        label: value,
                      }))}
                      fixedIcon="calendar-clock"
                      value={INTERVAL[props.interval]}
                      onChange={interval =>
                        onChange()({
                          interval: Object.keys(INTERVAL).find(
                            key => INTERVAL[key as Interval] === interval,
                          ) as Interval,
                        })
                      }
                    />
                  </Mini.Section>
                </TooltipComponent>
              </Mini.Menu>
            </div>
          )}
        </Show>
        <div class={statsClass}>
          <Stat
            number={displayNumber()}
            unit={props.measure}
            period={displayPeriod()}
            delta={deltaPercentage()}
            loading={getStats.loading}
          />
        </div>
        <div class={chartRecipe({ loading: getStats.loading })}>
          <div class={chartContainerRecipe({ loading: getStats.loading })}>
            <Bar data={chartData()} options={chartOptions} height={50} />
          </div>
          <div class={dateRangeClass}>
            <Show when={getDateRange()}>
              {dateRange => (
                <>
                  <span>{dateRange().start}</span>
                  <span>{dateRange().end}</span>
                </>
              )}
            </Show>
          </div>
        </div>
      </div>
    </div>
  )
}

type StatProps = {
  number: number | string
  unit: string
  period: string
  loading?: boolean
  delta: number
}

function Stat(props: StatProps) {
  const trend = createMemo(() => {
    if (props.delta === 0) return "neutral"

    return props.delta > 0 ? "up" : "down"
  })

  return (
    <Box>
      <span class={statRecipe({ loading: props.loading })}>
        <span class={statNumClass}>{props.number}</span>
        <span class={statTextClass}>
          {props.unit}
          <br />
          {props.period}
        </span>
      </span>
      <span class={deltaRecipe({ trend: trend() })}>
        <Show when={props.delta !== Number.POSITIVE_INFINITY}>
          <Switch>
            <Match when={trend() === "up"}>
              <Icon name="arrow-up" size="18" color="emerald600" />
            </Match>
            <Match when={trend() === "down"}>
              <Icon name="arrow-down" size="18" color="rose600" />
            </Match>
            <Match when={trend() === "neutral"}>
              <Icon name="equal" size="18" color="indigo600" />
            </Match>
          </Switch>
          {`${props.delta}%`}
        </Show>
      </span>
    </Box>
  )
}
